import { supabase } from '../../shared/supabase/supabaseClient';
import type {
  CommissionPlan,
  CommissionRule,
  CommissionAssignment,
  CommissionEarning,
  CommissionSummary,
  MyCommissionSummary,
  CommissionAdjustment,
  CommissionSettlement,
  SettlementDetail,
  CommissionRuleType,
  CommissionScope,
  CommissionBase,
  AttributionRole,
  AdjustmentType,
  PaymentMethod,
  TierConfig,
} from '../domain/commissionTypes';

// ============================================================================
// Plans
// ============================================================================

export async function listCommissionPlans(branchId?: string | null): Promise<CommissionPlan[]> {
  const { data, error } = await supabase.rpc('list_commission_plans', {
    p_branch_id: branchId ?? null,
  });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{
    id: string; name: string; description: string | null; branch_id: string | null;
    is_active: boolean; is_default: boolean; effective_from: string; effective_to: string | null;
    rule_count: number; assignment_count: number;
  }>;
  return rows.map((r) => ({
    id: r.id, name: r.name, description: r.description, branchId: r.branch_id,
    isActive: r.is_active, isDefault: r.is_default, effectiveFrom: r.effective_from,
    effectiveTo: r.effective_to, ruleCount: Number(r.rule_count),
    assignmentCount: Number(r.assignment_count),
  }));
}

export async function deleteCommissionPlan(planId: string): Promise<void> {
  const { error } = await supabase.from('commission_plans').delete().eq('id', planId);
  if (error) throw new Error(error.message);
}

// ============================================================================
// Rules
// ============================================================================

export async function listCommissionRules(planId: string): Promise<CommissionRule[]> {
  const { data, error } = await supabase.rpc('list_commission_rules', { p_plan_id: planId });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{
    id: string; rule_type: string; scope: string; scope_ref: string | null;
    rate_percent: number | null; fixed_amount: number | null; commission_base: string;
    tiers: TierConfig[]; attribution_role: string; split_percent: number;
    priority: number; is_active: boolean; effective_from: string; effective_to: string | null;
    day_of_week: number | null; branch_id: string | null;
  }>;
  return rows.map((r) => ({
    id: r.id, ruleType: r.rule_type as CommissionRuleType, scope: r.scope as CommissionScope,
    scopeRef: r.scope_ref, ratePercent: r.rate_percent != null ? Number(r.rate_percent) : null,
    fixedAmount: r.fixed_amount != null ? Number(r.fixed_amount) : null,
    commissionBase: r.commission_base as CommissionBase,
    tiers: Array.isArray(r.tiers) ? r.tiers : [],
    attributionRole: r.attribution_role as AttributionRole,
    splitPercent: Number(r.split_percent), priority: r.priority,
    isActive: r.is_active, effectiveFrom: r.effective_from, effectiveTo: r.effective_to,
    dayOfWeek: r.day_of_week, branchId: r.branch_id,
  }));
}

export async function createCommissionRule(params: {
  planId: string;
  ruleType: CommissionRuleType;
  scope: CommissionScope;
  scopeRef?: string | null;
  ratePercent?: number | null;
  fixedAmount?: number | null;
  commissionBase?: CommissionBase;
  tiers?: TierConfig[];
  attributionRole?: AttributionRole;
  splitPercent?: number;
  priority?: number;
}): Promise<string> {
  const { data, error } = await supabase.from('commission_rules').insert({
    plan_id: params.planId,
    rule_type: params.ruleType,
    scope: params.scope,
    scope_ref: params.scopeRef ?? null,
    rate_percent: params.ratePercent ?? null,
    fixed_amount: params.fixedAmount ?? null,
    commission_base: params.commissionBase ?? 'line_total',
    tiers: params.tiers ?? [],
    attribution_role: params.attributionRole ?? 'performed_by',
    split_percent: params.splitPercent ?? 100,
    priority: params.priority ?? 0,
  }).select('id').single();
  if (error) throw new Error(error.message);
  return data.id;
}

export async function deleteCommissionRule(ruleId: string): Promise<void> {
  const { error } = await supabase.from('commission_rules').delete().eq('id', ruleId);
  if (error) throw new Error(error.message);
}

export async function updateCommissionRule(ruleId: string, updates: {
  ruleType?: CommissionRuleType;
  scope?: CommissionScope;
  scopeRef?: string | null;
  ratePercent?: number | null;
  fixedAmount?: number | null;
  commissionBase?: CommissionBase;
  tiers?: TierConfig[];
  attributionRole?: AttributionRole;
  splitPercent?: number;
  priority?: number;
  isActive?: boolean;
  dayOfWeek?: number | null;
  branchId?: string | null;
}): Promise<void> {
  // #724: mapear camelCase → snake_case para que el update toque las columnas correctas
  const snakeUpdate: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if ('ruleType' in updates) snakeUpdate.rule_type = updates.ruleType;
  if ('scope' in updates) snakeUpdate.scope = updates.scope;
  if ('scopeRef' in updates) snakeUpdate.scope_ref = updates.scopeRef;
  if ('ratePercent' in updates) snakeUpdate.rate_percent = updates.ratePercent;
  if ('fixedAmount' in updates) snakeUpdate.fixed_amount = updates.fixedAmount;
  if ('commissionBase' in updates) snakeUpdate.commission_base = updates.commissionBase;
  if ('tiers' in updates) snakeUpdate.tiers = updates.tiers;
  if ('attributionRole' in updates) snakeUpdate.attribution_role = updates.attributionRole;
  if ('splitPercent' in updates) snakeUpdate.split_percent = updates.splitPercent;
  if ('priority' in updates) snakeUpdate.priority = updates.priority;
  if ('isActive' in updates) snakeUpdate.is_active = updates.isActive;
  if ('dayOfWeek' in updates) snakeUpdate.day_of_week = updates.dayOfWeek;
  if ('branchId' in updates) snakeUpdate.branch_id = updates.branchId;

  const { error } = await supabase.from('commission_rules').update(snakeUpdate).eq('id', ruleId);
  if (error) throw new Error(error.message);
}

export async function duplicateCommissionPlan(params: {
  sourcePlanId: string;
  newName: string;
}): Promise<string> {
  // Fetch source plan
  const { data: planRow, error: planErr } = await supabase
    .from('commission_plans')
    .select('*')
    .eq('id', params.sourcePlanId)
    .single();
  if (planErr) throw new Error(planErr.message);

  // Fetch source rules
  const rules = await listCommissionRules(params.sourcePlanId);

  // Create new plan via saveCommissionPlan (includes rules)
  const result = await saveCommissionPlan({
    name: params.newName,
    description: planRow.description,
    branchId: planRow.branch_id,
    isDefault: false,
    rules: rules.map((r) => ({
      ruleType: r.ruleType,
      scope: r.scope,
      scopeRef: r.scopeRef,
      ratePercent: r.ratePercent,
      fixedAmount: r.fixedAmount,
      commissionBase: r.commissionBase,
      tiers: r.tiers,
      attributionRole: r.attributionRole,
      splitPercent: r.splitPercent,
      priority: r.priority,
      dayOfWeek: r.dayOfWeek,
      branchId: r.branchId,
      isActive: r.isActive,
    })),
  });
  if (!result.ok || !result.planId) throw new Error(result.error ?? 'Error al duplicar plan');
  return result.planId;
}

// ============================================================================
// Assignments
// ============================================================================

export async function listCommissionAssignments(planId?: string | null): Promise<CommissionAssignment[]> {
  const { data, error } = await supabase.rpc('list_commission_assignments', {
    p_plan_id: planId ?? null,
  });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{
    id: string; plan_id: string; plan_name: string; employee_user_id: string;
    employee_name: string; is_active: boolean; assigned_at: string;
  }>;
  return rows.map((r) => ({
    id: r.id, planId: r.plan_id, planName: r.plan_name,
    employeeUserId: r.employee_user_id, employeeName: r.employee_name,
    isActive: r.is_active, assignedAt: r.assigned_at,
  }));
}

export async function assignEmployeeToPlan(params: {
  planId: string;
  employeeUserId: string;
  organizationId: string;
}): Promise<void> {
  const { data, error } = await supabase.rpc('assign_commission_plan', {
    p_plan_id: params.planId,
    p_employee_user_id: params.employeeUserId,
  });
  if (error) throw new Error(error.message);
  const body = data as { ok?: boolean; error?: string } | null;
  if (body && body.ok === false) {
    throw new Error(body.error ?? 'No se pudo asignar el plan de comisión.');
  }
}

export async function unassignEmployeeFromPlan(planId: string, employeeUserId: string): Promise<void> {
  const { data, error } = await supabase.rpc('unassign_commission_plan', {
    p_plan_id: planId,
    p_employee_user_id: employeeUserId,
  });
  if (error) throw new Error(error.message);
  const body = data as { ok?: boolean; error?: string } | null;
  if (body && body.ok === false) {
    throw new Error(body.error ?? 'No se pudo quitar el plan de comisión.');
  }
}

/** Activa/desactiva earns_commission (owner Comisiones). Al apagar limpia assignments. */
export async function setEmployeeEarnsCommission(
  employeeUserId: string,
  earns: boolean
): Promise<void> {
  const { data, error } = await supabase.rpc('set_employee_earns_commission', {
    p_employee_user_id: employeeUserId,
    p_earns: earns,
  });
  if (error) throw new Error(error.message);
  const body = data as { ok?: boolean; error?: string } | null;
  if (body && body.ok === false) {
    throw new Error(body.error ?? 'No se pudo actualizar si genera comisiones.');
  }
}

// ============================================================================
// Earnings
// ============================================================================

export async function listCommissionEarnings(params: {
  branchId?: string | null;
  employeeUserId?: string | null;
  fromIso?: string | null;
  toIso?: string | null;
  status?: string | null;
}): Promise<CommissionEarning[]> {
  const { data, error } = await supabase.rpc('list_commission_earnings', {
    p_branch_id: params.branchId ?? null,
    p_employee_user_id: params.employeeUserId ?? null,
    p_from: params.fromIso ?? null,
    p_to: params.toIso ?? null,
    p_status: params.status ?? null,
  });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{
    id: string; employee_user_id: string; employee_name: string;
    source_type: string; source_id: string; source_ref: string | null;
    commissionable_amount: number; commission_amount: number; rate_percent: number | null;
    rule_type: string | null; description: string | null; earned_at: string;
    status: string; settlement_id: string | null;
  }>;
  return rows.map((r) => ({
    id: r.id, employeeUserId: r.employee_user_id, employeeName: r.employee_name,
    sourceType: r.source_type as CommissionEarning['sourceType'],
    sourceId: r.source_id, sourceRef: r.source_ref,
    commissionableAmount: Number(r.commissionable_amount),
    commissionAmount: Number(r.commission_amount),
    ratePercent: r.rate_percent != null ? Number(r.rate_percent) : null,
    ruleType: r.rule_type, description: r.description, earnedAt: r.earned_at,
    status: r.status as CommissionEarning['status'], settlementId: r.settlement_id,
  }));
}

export async function listCommissionSummaries(params: {
  branchId?: string | null;
  fromIso?: string | null;
  toIso?: string | null;
}): Promise<CommissionSummary[]> {
  const { data, error } = await supabase.rpc('list_commission_summaries', {
    p_branch_id: params.branchId ?? null,
    p_from: params.fromIso ?? null,
    p_to: params.toIso ?? null,
  });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{
    employee_user_id: string; employee_name: string; earnings_count: number;
    gross_earnings: number; pending_earnings: number; settled_earnings: number;
    adjustments_total: number; net_payable: number;
  }>;
  return rows.map((r) => ({
    employeeUserId: r.employee_user_id, employeeName: r.employee_name,
    earningsCount: Number(r.earnings_count), grossEarnings: Number(r.gross_earnings),
    pendingEarnings: Number(r.pending_earnings), settledEarnings: Number(r.settled_earnings),
    adjustmentsTotal: Number(r.adjustments_total), netPayable: Number(r.net_payable),
  }));
}

export async function upsertCommissionEarningsForSale(saleId: string): Promise<{ ok: boolean; earningsCreated: number }> {
  const { data, error } = await supabase.rpc('upsert_commission_earnings_for_sale', {
    p_sale_id: saleId,
  });
  if (error) throw new Error(error.message);
  const result = data as { ok: boolean; earnings_created: number };
  return { ok: result.ok, earningsCreated: result.earnings_created };
}

export async function listCommissionEarningsForSale(params: {
  saleId: string;
  employeeUserId?: string | null;
}): Promise<CommissionEarning[]> {
  const { data, error } = await supabase.rpc('list_commission_earnings_for_sale', {
    p_sale_id: params.saleId,
    p_employee_user_id: params.employeeUserId ?? null,
  });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{
    id: string; employee_user_id: string; employee_name: string;
    source_type: string; source_id: string; source_ref: string | null;
    commissionable_amount: number; commission_amount: number; rate_percent: number | null;
    rule_type: string | null; description: string | null; earned_at: string;
    status: string; settlement_id: string | null;
  }>;
  return rows.map((r) => ({
    id: r.id, employeeUserId: r.employee_user_id, employeeName: r.employee_name,
    sourceType: r.source_type as CommissionEarning['sourceType'],
    sourceId: r.source_id, sourceRef: r.source_ref,
    commissionableAmount: Number(r.commissionable_amount),
    commissionAmount: Number(r.commission_amount),
    ratePercent: r.rate_percent != null ? Number(r.rate_percent) : null,
    ruleType: r.rule_type, description: r.description, earnedAt: r.earned_at,
    status: r.status as CommissionEarning['status'], settlementId: r.settlement_id,
  }));
}

/**
 * Devuelve un Set de sale_ids (source_ref) que tienen comisiones
 * para una sucursal y rango de fechas. Usado para filtrar/marcar ventas.
 */
export async function listSaleIdsWithCommissions(params: {
  branchId: string;
  fromIso?: string | null;
  toIso?: string | null;
}): Promise<Set<string>> {
  const { data, error } = await supabase.rpc('list_commission_earnings', {
    p_branch_id: params.branchId,
    p_from: params.fromIso ?? null,
    p_to: params.toIso ?? null,
    p_status: null,
  });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{ source_ref: string | null }>;
  const saleIds = new Set<string>();
  for (const r of rows) {
    if (r.source_ref) saleIds.add(r.source_ref);
  }
  return saleIds;
}

// ============================================================================
// My Commissions (employee self-service)
// ============================================================================

export async function listMyCommissions(params: {
  fromIso?: string | null;
  toIso?: string | null;
  status?: string | null;
}): Promise<CommissionEarning[]> {
  const { data, error } = await supabase.rpc('list_my_commissions', {
    p_from: params.fromIso ?? null,
    p_to: params.toIso ?? null,
    p_status: params.status ?? null,
  });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{
    id: string; employee_user_id: string; employee_name: string;
    source_type: string; source_id: string; source_ref: string | null;
    commissionable_amount: number; commission_amount: number; rate_percent: number | null;
    rule_type: string | null; description: string | null; earned_at: string;
    status: string; settlement_id: string | null;
  }>;
  return rows.map((r) => ({
    id: r.id, employeeUserId: r.employee_user_id, employeeName: r.employee_name,
    sourceType: r.source_type as CommissionEarning['sourceType'],
    sourceId: r.source_id, sourceRef: r.source_ref,
    commissionableAmount: Number(r.commissionable_amount),
    commissionAmount: Number(r.commission_amount),
    ratePercent: r.rate_percent != null ? Number(r.rate_percent) : null,
    ruleType: r.rule_type, description: r.description, earnedAt: r.earned_at,
    status: r.status as CommissionEarning['status'], settlementId: r.settlement_id,
  }));
}

export async function listMyCommissionSummary(params: {
  fromIso?: string | null;
  toIso?: string | null;
}): Promise<MyCommissionSummary | null> {
  const { data, error } = await supabase.rpc('list_my_commission_summary', {
    p_from: params.fromIso ?? null,
    p_to: params.toIso ?? null,
  });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{
    earnings_count: number; gross_earnings: number; pending_earnings: number;
    settled_earnings: number; adjustments_total: number; net_payable: number;
  }>;
  if (rows.length === 0) return null;
  const r = rows[0];
  return {
    earningsCount: Number(r.earnings_count), grossEarnings: Number(r.gross_earnings),
    pendingEarnings: Number(r.pending_earnings), settledEarnings: Number(r.settled_earnings),
    adjustmentsTotal: Number(r.adjustments_total), netPayable: Number(r.net_payable),
  };
}

export async function listMyAdjustments(params: {
  fromIso?: string | null;
  toIso?: string | null;
}): Promise<CommissionAdjustment[]> {
  const { data, error } = await supabase.rpc('list_my_adjustments', {
    p_from: params.fromIso ?? null,
    p_to: params.toIso ?? null,
  });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{
    id: string; adjustment_type: string; amount: number; product_id: string | null;
    quantity: number | null; unit_value: number | null; reason: string;
    description: string | null; effective_date: string; status: string;
    settlement_id: string | null; created_at: string;
  }>;
  // #765: el RPC filtra por auth.uid() — los adjustments son del usuario actual.
  // Usar el session user en vez de strings vacíos.
  const {
    data: { user },
  } = await supabase.auth.getUser();
  const employeeUserId = user?.id ?? '';
  return rows.map((r) => ({
    id: r.id, employeeUserId, employeeName: '',
    adjustmentType: r.adjustment_type as AdjustmentType, amount: Number(r.amount),
    productId: r.product_id, quantity: r.quantity != null ? Number(r.quantity) : null,
    unitValue: r.unit_value != null ? Number(r.unit_value) : null,
    reason: r.reason, description: r.description, effectiveDate: r.effective_date,
    status: r.status as CommissionAdjustment['status'], settlementId: r.settlement_id,
    createdBy: null, createdAt: r.created_at,
  }));
}

// ============================================================================
// Adjustments (owner)
// ============================================================================

export async function listCommissionAdjustments(params: {
  branchId?: string | null;
  employeeUserId?: string | null;
  fromIso?: string | null;
  toIso?: string | null;
  status?: string | null;
}): Promise<CommissionAdjustment[]> {
  const { data, error } = await supabase.rpc('list_commission_adjustments', {
    p_branch_id: params.branchId ?? null,
    p_employee_user_id: params.employeeUserId ?? null,
    p_from: params.fromIso ?? null,
    p_to: params.toIso ?? null,
    p_status: params.status ?? null,
  });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{
    id: string; employee_user_id: string; employee_name: string; adjustment_type: string;
    amount: number; product_id: string | null; quantity: number | null; unit_value: number | null;
    reason: string; description: string | null; effective_date: string;
    status: string; settlement_id: string | null; created_by: string | null; created_at: string;
  }>;
  return rows.map((r) => ({
    id: r.id, employeeUserId: r.employee_user_id, employeeName: r.employee_name,
    adjustmentType: r.adjustment_type as AdjustmentType, amount: Number(r.amount),
    productId: r.product_id, quantity: r.quantity != null ? Number(r.quantity) : null,
    unitValue: r.unit_value != null ? Number(r.unit_value) : null,
    reason: r.reason, description: r.description, effectiveDate: r.effective_date,
    status: r.status as CommissionAdjustment['status'], settlementId: r.settlement_id,
    createdBy: r.created_by, createdAt: r.created_at,
  }));
}

export async function createCommissionAdjustment(params: {
  organizationId: string;
  branchId?: string | null;
  employeeUserId: string;
  adjustmentType: AdjustmentType;
  amount: number;
  reason: string;
  description?: string | null;
  productId?: string | null;
  quantity?: number | null;
  unitValue?: number | null;
}): Promise<string> {
  // #726: validar campos según adjustmentType
  if (!params.reason.trim()) throw new Error('El motivo es obligatorio.');
  if (!Number.isFinite(params.amount) || params.amount === 0) {
    throw new Error('El monto debe ser un número distinto de cero.');
  }
  if (params.adjustmentType === 'product_withdrawal') {
    if (!params.productId) throw new Error('Los retiros de producto requieren productId.');
    if (params.quantity == null || params.quantity <= 0) throw new Error('Los retiros de producto requieren quantity > 0.');
    if (params.unitValue == null || params.unitValue <= 0) throw new Error('Los retiros de producto requieren unitValue > 0.');
  }
  if (params.adjustmentType === 'advance' && params.productId) {
    // advance no debería tener productId — lo limpiamos
    params.productId = null;
  }

  const { data, error } = await supabase.from('commission_adjustments').insert({
    organization_id: params.organizationId,
    branch_id: params.branchId ?? null,
    employee_user_id: params.employeeUserId,
    adjustment_type: params.adjustmentType,
    amount: params.amount,
    reason: params.reason,
    description: params.description ?? null,
    product_id: params.productId ?? null,
    quantity: params.quantity ?? null,
    unit_value: params.unitValue ?? null,
    created_by: (await supabase.auth.getUser()).data.user?.id ?? null,
  }).select('id').single();
  if (error) throw new Error(error.message);
  return data.id;
}

export async function voidCommissionAdjustment(adjustmentId: string, reason: string): Promise<void> {
  // #725: validar que el adjustment esté pending antes de anular
  const { data: existing, error: fetchErr } = await supabase
    .from('commission_adjustments')
    .select('status')
    .eq('id', adjustmentId)
    .maybeSingle();
  if (fetchErr) throw new Error(fetchErr.message);
  if (!existing) throw new Error('El ajuste no existe.');
  if (existing.status !== 'pending') {
    throw new Error(`No se puede anular un ajuste en estado "${existing.status}". Solo se pueden anular ajustes pendientes.`);
  }
  const { error } = await supabase.from('commission_adjustments').update({
    status: 'voided',
    voided_at: new Date().toISOString(),
    void_reason: reason,
  }).eq('id', adjustmentId);
  if (error) throw new Error(error.message);
}

// ============================================================================
// Settlements
// ============================================================================

export async function listCommissionSettlements(params: {
  branchId?: string | null;
  employeeUserId?: string | null;
  status?: string | null;
  fromIso?: string | null;
  toIso?: string | null;
}): Promise<CommissionSettlement[]> {
  const { data, error } = await supabase.rpc('list_commission_settlements', {
    p_branch_id: params.branchId ?? null,
    p_employee_user_id: params.employeeUserId ?? null,
    p_status: params.status ?? null,
    p_from: params.fromIso ?? null,
    p_to: params.toIso ?? null,
  });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{
    id: string; employee_user_id: string; employee_name: string; branch_id: string | null;
    period_from: string; period_to: string; gross_earnings: number; adjustments_total: number;
    net_payable: number; status: string; approved_at: string | null; paid_at: string | null;
    payment_method: string | null; payment_ref: string | null; note: string | null; created_at: string;
  }>;
  return rows.map((r) => ({
    id: r.id, employeeUserId: r.employee_user_id, employeeName: r.employee_name,
    branchId: r.branch_id, periodFrom: r.period_from, periodTo: r.period_to,
    grossEarnings: Number(r.gross_earnings), adjustmentsTotal: Number(r.adjustments_total),
    netPayable: Number(r.net_payable), status: r.status as CommissionSettlement['status'],
    approvedAt: r.approved_at, paidAt: r.paid_at,
    paymentMethod: r.payment_method as PaymentMethod | null,
    paymentRef: r.payment_ref, note: r.note, createdAt: r.created_at,
  }));
}

export async function createCommissionSettlement(params: {
  employeeUserId: string;
  periodFrom: string;
  periodTo: string;
  branchId?: string | null;
  note?: string | null;
}): Promise<{ ok: boolean; settlementId?: string; gross: number; adjustments: number; net: number; error?: string }> {
  const { data, error } = await supabase.rpc('create_commission_settlement', {
    p_employee_user_id: params.employeeUserId,
    p_period_from: params.periodFrom,
    p_period_to: params.periodTo,
    p_branch_id: params.branchId ?? null,
    p_note: params.note ?? null,
  });
  if (error) throw new Error(error.message);
  const result = data as { ok: boolean; settlement_id?: string; gross: number; adjustments: number; net: number; error?: string };
  return {
    ok: result.ok,
    settlementId: result.settlement_id,
    gross: Number(result.gross),
    adjustments: Number(result.adjustments),
    net: Number(result.net),
    error: result.error,
  };
}

export async function approveCommissionSettlement(settlementId: string): Promise<{ ok: boolean; error?: string }> {
  const { data, error } = await supabase.rpc('approve_commission_settlement', {
    p_settlement_id: settlementId,
  });
  if (error) throw new Error(error.message);
  return data as { ok: boolean; error?: string };
}

export async function payCommissionSettlement(params: {
  settlementId: string;
  paymentMethod: PaymentMethod;
  paymentRef?: string | null;
  note?: string | null;
  cashSessionId?: string | null;
}): Promise<{ ok: boolean; error?: string; cashOutflowId?: string | null; expenseId?: string | null }> {
  const { data, error } = await supabase.rpc('pay_commission_settlement', {
    p_settlement_id: params.settlementId,
    p_payment_method: params.paymentMethod,
    p_payment_ref: params.paymentRef ?? null,
    p_note: params.note ?? null,
    p_cash_session_id: params.cashSessionId ?? null,
  });
  if (error) throw new Error(error.message);
  const result = data as {
    ok: boolean;
    error?: string;
    cash_outflow_id?: string | null;
    expense_id?: string | null;
  };
  return {
    ok: result.ok,
    error: result.error,
    cashOutflowId: result.cash_outflow_id ?? null,
    expenseId: result.expense_id ?? null,
  };
}

export async function cancelCommissionSettlement(
  settlementId: string,
  voidReason: string,
): Promise<{ ok: boolean; error?: string; idempotent?: boolean }> {
  const { data, error } = await supabase.rpc('cancel_commission_settlement', {
    p_settlement_id: settlementId,
    p_void_reason: voidReason,
  });
  if (error) throw new Error(error.message);
  return data as { ok: boolean; error?: string; idempotent?: boolean };
}

export async function voidCommissionSettlement(settlementId: string, voidReason: string): Promise<{ ok: boolean; error?: string }> {
  const { data, error } = await supabase.rpc('void_commission_settlement', {
    p_settlement_id: settlementId,
    p_void_reason: voidReason,
  });
  if (error) throw new Error(error.message);
  return data as { ok: boolean; error?: string };
}

export async function getCommissionSettlementDetail(settlementId: string): Promise<SettlementDetail> {
  const { data, error } = await supabase.rpc('get_commission_settlement_detail', {
    p_settlement_id: settlementId,
  });
  if (error) throw new Error(error.message);
  const result = data as unknown as Partial<SettlementDetail> & { ok?: boolean; error?: string };
  if (result && result.ok === false) {
    throw new Error(result.error ?? 'No se pudo cargar el detalle de la liquidación.');
  }
  return result as SettlementDetail;
}

// ============================================================================
// New RPCs (v2)
// ============================================================================

export async function saveCommissionPlan(params: {
  name: string;
  planId?: string | null;
  description?: string | null;
  branchId?: string | null;
  isDefault?: boolean;
  rules?: Array<{
    id?: string | null;
    ruleType?: string;
    scope?: string;
    scopeRef?: string | null;
    ratePercent?: number | null;
    fixedAmount?: number | null;
    commissionBase?: string;
    tiers?: TierConfig[];
    attributionRole?: string;
    splitPercent?: number;
    priority?: number;
    dayOfWeek?: number | null;
    branchId?: string | null;
    isActive?: boolean;
  }>;
}): Promise<{ ok: boolean; planId?: string; error?: string }> {
  const { data, error } = await supabase.rpc('save_commission_plan', {
    p_name: params.name,
    p_plan_id: params.planId ?? null,
    p_description: params.description ?? null,
    p_branch_id: params.branchId ?? null,
    p_is_default: params.isDefault ?? false,
    p_rules: params.rules ?? [],
  });
  if (error) throw new Error(error.message);
  const result = data as { ok: boolean; plan_id?: string; error?: string };
  return { ok: result.ok, planId: result.plan_id, error: result.error };
}

export async function voidEarningsForSale(saleId: string): Promise<{ ok: boolean; earningsVoided: number }> {
  const { data, error } = await supabase.rpc('void_earnings_for_sale', {
    p_sale_id: saleId,
  });
  if (error) throw new Error(error.message);
  const result = data as { ok: boolean; earnings_voided: number };
  return { ok: result.ok, earningsVoided: result.earnings_voided };
}

export async function registerAdvance(params: {
  employeeUserId: string;
  amount: number;
  reason: string;
  branchId?: string | null;
  description?: string | null;
  operationId?: string | null;
}): Promise<{ ok: boolean; adjustmentId?: string; error?: string; idempotent?: boolean }> {
  const { data, error } = await supabase.rpc('register_advance', {
    p_employee_user_id: params.employeeUserId,
    p_amount: params.amount,
    p_reason: params.reason,
    p_branch_id: params.branchId ?? null,
    p_description: params.description ?? null,
    p_operation_id: params.operationId ?? crypto.randomUUID(),
  });
  if (error) throw new Error(error.message);
  const result = data as {
    ok: boolean;
    adjustment_id?: string;
    error?: string;
    idempotent?: boolean;
  };
  return {
    ok: result.ok,
    adjustmentId: result.adjustment_id,
    error: result.error,
    idempotent: result.idempotent,
  };
}

export async function registerProductWithdrawal(params: {
  employeeUserId: string;
  productId: string;
  quantity: number;
  unitValue: number;
  reason: string;
  branchId?: string | null;
  operationId?: string | null;
}): Promise<{ ok: boolean; adjustmentId?: string; amount?: number; error?: string; idempotent?: boolean }> {
  const { data, error } = await supabase.rpc('register_product_withdrawal', {
    p_employee_user_id: params.employeeUserId,
    p_product_id: params.productId,
    p_quantity: params.quantity,
    p_unit_value: params.unitValue,
    p_reason: params.reason,
    p_branch_id: params.branchId ?? null,
    p_operation_id: params.operationId ?? crypto.randomUUID(),
  });
  if (error) throw new Error(error.message);
  const result = data as {
    ok: boolean;
    adjustment_id?: string;
    amount?: number;
    error?: string;
    idempotent?: boolean;
  };
  return {
    ok: result.ok,
    adjustmentId: result.adjustment_id,
    amount: result.amount != null ? Number(result.amount) : undefined,
    error: result.error,
    idempotent: result.idempotent,
  };
}

export async function simulateCommission(params: {
  employeeUserId: string;
  amount: number;
  productId?: string | null;
  quantity?: number;
  branchId?: string | null;
}): Promise<{
  ok: boolean; planId?: string; ruleId?: string; ruleType?: string;
  base?: number; commission?: number; ratePercent?: number; error?: string;
}> {
  const { data, error } = await supabase.rpc('simulate_commission', {
    p_employee_user_id: params.employeeUserId,
    p_amount: params.amount,
    p_product_id: params.productId ?? null,
    p_quantity: params.quantity ?? 1,
    p_branch_id: params.branchId ?? null,
  });
  if (error) throw new Error(error.message);
  const result = data as {
    ok: boolean; plan_id?: string; rule_id?: string; rule_type?: string;
    base?: number; commission?: number; rate_percent?: number; error?: string;
  };
  return {
    ok: result.ok, planId: result.plan_id, ruleId: result.rule_id,
    ruleType: result.rule_type, base: result.base != null ? Number(result.base) : undefined,
    commission: result.commission != null ? Number(result.commission) : undefined,
    ratePercent: result.rate_percent != null ? Number(result.rate_percent) : undefined,
    error: result.error,
  };
}

export async function simulateCommissionsBatch(items: Array<{
  employeeUserId: string;
  amount: number;
  productId?: string | null;
  quantity?: number;
  branchId?: string | null;
}>): Promise<{ ok: boolean; total: number; count: number; results: Array<{ ok: boolean; commission?: number }> }> {
  const { data, error } = await supabase.rpc('simulate_commissions_batch', {
    p_items: items.map((i) => ({
      employee_user_id: i.employeeUserId,
      amount: i.amount,
      product_id: i.productId ?? null,
      quantity: i.quantity ?? 1,
      branch_id: i.branchId ?? null,
    })),
  });
  if (error) throw new Error(error.message);
  const result = data as { ok: boolean; total: number; count: number; items: Array<{ ok: boolean; commission?: number }> };
  return {
    ok: result.ok,
    total: result.total,
    count: result.count,
    results: result.items ?? [],
  };
}

export async function listBoothRentals(params?: {
  branchId?: string | null;
  employeeUserId?: string | null;
  status?: string | null;
}): Promise<Array<{
  id: string; employeeUserId: string; employeeName: string; branchId: string | null;
  periodFrom: string; periodTo: string; amount: number; frequency: string;
  status: string; note: string | null; createdAt: string;
}>> {
  const { data, error } = await supabase.rpc('list_booth_rentals', {
    p_branch_id: params?.branchId ?? null,
    p_employee_user_id: params?.employeeUserId ?? null,
    p_status: params?.status ?? null,
  });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{
    id: string; employee_user_id: string; employee_name: string; branch_id: string | null;
    period_from: string; period_to: string; amount: number; frequency: string;
    status: string; note: string | null; created_at: string;
  }>;
  return rows.map((r) => ({
    id: r.id, employeeUserId: r.employee_user_id, employeeName: r.employee_name,
    branchId: r.branch_id, periodFrom: r.period_from, periodTo: r.period_to,
    amount: Number(r.amount), frequency: r.frequency, status: r.status,
    note: r.note, createdAt: r.created_at,
  }));
}

export async function saveBoothRental(params: {
  employeeUserId: string;
  periodFrom: string;
  periodTo: string;
  amount: number;
  id?: string | null;
  frequency?: string;
  branchId?: string | null;
  note?: string | null;
}): Promise<{ ok: boolean; id?: string; error?: string }> {
  const { data, error } = await supabase.rpc('save_booth_rental', {
    p_employee_user_id: params.employeeUserId,
    p_period_from: params.periodFrom,
    p_period_to: params.periodTo,
    p_amount: params.amount,
    p_id: params.id ?? null,
    p_frequency: params.frequency ?? 'monthly',
    p_branch_id: params.branchId ?? null,
    p_note: params.note ?? null,
  });
  if (error) throw new Error(error.message);
  const result = data as { ok: boolean; id?: string; error?: string };
  return { ok: result.ok, id: result.id, error: result.error };
}

export async function deleteBoothRental(rentalId: string): Promise<{ ok: boolean; error?: string }> {
  const { data, error } = await supabase.rpc('delete_booth_rental', {
    p_rental_id: rentalId,
  });
  if (error) throw new Error(error.message);
  return data as { ok: boolean; error?: string };
}
